import { useState } from "react";
import type { FieldConfig } from "@/lib/adminFields";
import { defaultValueFor } from "@/lib/adminFields";
import ImageField from "./ImageField";

interface ResourceFormProps {
  fields: FieldConfig[];
  initial: Record<string, unknown>;
  submitLabel?: string;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100";

export default function ResourceForm({
  fields,
  initial,
  submitLabel = "Guardar",
  onSubmit,
  onCancel,
}: ResourceFormProps) {
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const base: Record<string, unknown> = {};
    fields.forEach((field) => {
      base[field.name] = initial[field.name] ?? defaultValueFor(field.type);
    });
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (name: string, value: unknown) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    const missing = fields.find((field) => {
      if (!field.required) return false;
      const value = values[field.name];
      if (Array.isArray(value)) return value.length === 0;
      if (typeof value === "string") return value.trim() === "";
      return value === null || value === undefined;
    });
    if (missing) {
      setError(`Completa el campo "${missing.label}".`);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(values);
    } catch {
      setError("No pudimos guardar los cambios. Revisa los datos e intenta nuevamente.");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: FieldConfig) => {
    const value = values[field.name];

    if (field.type === "image") {
      return (
        <ImageField
          value={String(value ?? "")}
          onChange={(url) => set(field.name, url)}
          folder={field.folder}
        />
      );
    }

    if (field.type === "textarea") {
      return (
        <textarea
          value={String(value ?? "")}
          onChange={(event) => set(field.name, event.target.value)}
          placeholder={field.placeholder}
          rows={3}
          className={`${inputClass} resize-y`}
        />
      );
    }

    if (field.type === "select") {
      return (
        <select
          value={String(value ?? "")}
          onChange={(event) => set(field.name, event.target.value)}
          className={inputClass}
        >
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "switch") {
      const checked = Boolean(value);
      return (
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          onClick={() => set(field.name, !checked)}
          className={`inline-flex cursor-pointer items-center gap-2.5 rounded-md border px-3.5 py-2 text-sm font-label font-medium transition-colors ${
            checked
              ? "border-primary-300 bg-primary-50 text-primary-800"
              : "border-background-300 bg-background-50 text-foreground-600"
          }`}
        >
          <span
            className={`relative h-5 w-9 rounded-full transition-colors ${
              checked ? "bg-primary-500" : "bg-background-300"
            }`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-background-50 transition-all ${
                checked ? "left-[18px]" : "left-0.5"
              }`}
            ></span>
          </span>
          {checked ? "Activo" : "Oculto"}
        </button>
      );
    }

    if (field.type === "tags") {
      const list = Array.isArray(value) ? (value as string[]) : [];
      return (
        <input
          type="text"
          value={list.join(", ")}
          onChange={(event) =>
            set(
              field.name,
              event.target.value
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean),
            )
          }
          placeholder={field.placeholder ?? "Separadas por comas"}
          className={inputClass}
        />
      );
    }

    if (field.type === "number") {
      return (
        <input
          type="number"
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(event) =>
            set(field.name, event.target.value === "" ? 0 : Number(event.target.value))
          }
          placeholder={field.placeholder}
          className={inputClass}
        />
      );
    }

    if (field.type === "date") {
      return (
        <input
          type="date"
          value={String(value ?? "")}
          onChange={(event) => set(field.name, event.target.value || null)}
          className={inputClass}
        />
      );
    }

    return (
      <input
        type="text"
        value={String(value ?? "")}
        onChange={(event) => set(field.name, event.target.value)}
        placeholder={field.placeholder}
        className={inputClass}
      />
    );
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {fields.map((field) => (
          <div key={field.name} className={field.full ? "md:col-span-2" : ""}>
            <label className="mb-1.5 flex items-center gap-1 text-sm font-label font-medium text-foreground-700">
              {field.label}
              {field.required && <span className="text-primary-600">*</span>}
            </label>
            {renderField(field)}
            {field.help && <p className="mt-1 text-xs text-foreground-500">{field.help}</p>}
          </div>
        ))}
      </div>

      {error && (
        <p
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm text-primary-800"
        >
          <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
          {error}
        </p>
      )}

      <div className="mt-6 flex flex-col gap-3 border-t border-background-200 pt-5 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-label font-semibold text-foreground-700 transition-colors hover:bg-background-100"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground-950"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base leading-none" aria-hidden="true"></i>
              Guardando…
            </>
          ) : (
            <>
              <i className="ri-save-3-line text-base leading-none" aria-hidden="true"></i>
              {submitLabel}
            </>
          )}
        </button>
      </div>
    </form>
  );
}